import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Award, Download, Calendar, AlertTriangle } from 'lucide-react';

const Certificates = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      const response = await axiosInstance.get('/certificates');
      setCertificates(response.data);
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (certificate) => {
    try {
      const response = await axiosInstance.get(`/certificates/${certificate.id}/download`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${certificate.courseName}_Certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded successfully!');
    } catch (error) {
      console.error('Failed to download certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-yellow-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="mb-6"
          data-testid="back-to-dashboard-certs"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <h1 className="text-4xl font-bold text-slate-800 mb-8">My Certificates</h1>

        {certificates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="p-4 bg-green-50 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Active</p>
                  <p className="text-2xl font-bold text-green-800">
                    {certificates.filter(c => !c.isExpired && !c.isExpiringSoon).length}
                  </p>
                </div>
                <Award className="w-8 h-8 text-green-600" />
              </div>
            </Card>
            <Card className="p-4 bg-amber-50 border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700">Expiring Soon</p>
                  <p className="text-2xl font-bold text-amber-800">
                    {certificates.filter(c => c.isExpiringSoon).length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-amber-600" />
              </div>
            </Card>
            <Card className="p-4 bg-red-50 border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">Expired</p>
                  <p className="text-2xl font-bold text-red-800">
                    {certificates.filter(c => c.isExpired).length}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </Card>
          </div>
        )}

        {certificates.length === 0 ? (
          <Card className="p-12 text-center">
            <Award className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-lg text-slate-600">No certificates earned yet.</p>
            <p className="text-sm text-slate-500 mt-2">Complete courses and pass quizzes to earn certificates.</p>
          </Card>
        ) : (
          <div className="space-y-8">
            {certificates.map(cert => (
              <Card key={cert.id} className="p-8 bg-white border-2 border-orange-200">
                <div className="border-8 border-double border-orange-300 p-8 rounded-lg bg-gradient-to-br from-orange-50 to-yellow-50">
                  <div className="text-center space-y-6">
                    <Award className="w-20 h-20 text-orange-500 mx-auto" />
                    
                    <div>
                      <h2 className="text-3xl font-bold text-slate-800 mb-2">Certificate of Completion</h2>
                      <p className="text-slate-600">This certifies that</p>
                    </div>

                    <div className="py-4 border-b-2 border-orange-300">
                      <p className="text-4xl font-bold text-orange-600">{cert.userName}</p>
                    </div>

                    <div>
                      <p className="text-slate-600 mb-2">has successfully completed the course</p>
                      <p className="text-2xl font-semibold text-slate-800">{cert.courseName}</p>
                    </div>

                    <div className="flex justify-center items-center space-x-6 pt-4">
                      <div className="text-center">
                        <p className="text-sm text-slate-600">Score</p>
                        <p className="text-2xl font-bold text-green-600">{cert.score}%</p>
                      </div>
                      <div className="h-12 w-px bg-slate-300"></div>
                      <div className="text-center">
                        <p className="text-sm text-slate-600">Date Issued</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {new Date(cert.issuedAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      {cert.expiresAt && (
                        <>
                          <div className="h-12 w-px bg-slate-300"></div>
                          <div className="text-center">
                            <p className="text-sm text-slate-600">Expires On</p>
                            <p className="text-lg font-semibold text-slate-800">
                              {new Date(cert.expiresAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            {cert.isExpired && (
                              <Badge variant="destructive" className="mt-1">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Expired
                              </Badge>
                            )}
                            {cert.isExpiringSoon && !cert.isExpired && (
                              <Badge className="bg-amber-500 mt-1">
                                <Calendar className="w-3 h-3 mr-1" />
                                Expires in {cert.daysUntilExpiry} days
                              </Badge>
                            )}
                            {!cert.isExpired && !cert.isExpiringSoon && (
                              <Badge className="bg-green-600 mt-1">
                                Active
                              </Badge>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="pt-4">
                      <p className="text-sm text-slate-600">Certificate ID</p>
                      <p className="text-lg font-mono font-semibold text-slate-800">{cert.certificateId}</p>
                    </div>

                    <div className="pt-6">
                      <p className="text-xs text-slate-500 italic">SafetyMind AI Construction Safety Training</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-center">
                  <Button
                    onClick={() => downloadCertificate(cert)}
                    className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                    data-testid={`download-cert-${cert.id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Certificate
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificates;
